import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

/**
 * Modal dialog for naming and confirming an estimate save.
 * Handles both "save new" and "save-as-new" (copy of loaded) flows.
 */
export function SaveEstimateDialog({
  open,
  onOpenChange,
  saveName,
  setSaveName,
  loadedEstimateName,
  saving,
  onSave,
}) {
  const { t } = useTranslation();
  let confirmLabel;
  if (saving) confirmLabel = t('results.saving');
  else if (loadedEstimateName) confirmLabel = t('results.saveAsNew');
  else confirmLabel = t('results.save');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#DDE1E5] text-[#171A1D]">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {loadedEstimateName ? t('results.saveAsNew') : t('results.saveDialog')}
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-3">
          {loadedEstimateName && (
            <div className="flex items-start gap-2 text-xs text-[#66707A] bg-[#F5F6F7] border border-[#DDE1E5] rounded-lg px-3 py-2.5" data-testid="save-as-new-hint">
              <span className="shrink-0 mt-0.5">ℹ</span>
              <span>
                {t('results.saveAsNewHint', {
                  name: loadedEstimateName,
                  defaultValue: `A new estimate will be created. Your original "${loadedEstimateName}" estimate is unchanged.`,
                })}
              </span>
            </div>
          )}
          <div>
            <Label className="text-[#66707A]">{t('results.estimateName')}</Label>
            <Input
              value={saveName}
              onChange={e => setSaveName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && onSave()}
              className="mt-1.5 bg-white border-[#DDE1E5] text-[#171A1D]"
              placeholder={t('results.namePlaceholder')}
              data-testid="save-estimate-name-input"
              autoFocus
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-[#66707A] hover:text-[#171A1D]"
            data-testid="save-estimate-cancel-btn"
          >
            {t('results.cancel')}
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !saveName.trim()}
            className="bg-[#527F19] hover:bg-[#3F6114] text-white"
            data-testid="save-estimate-confirm-btn"
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
